import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import * as z from 'zod';

import { formIds } from '@/definitions/formIds';
import { compareDates, getDateFromSquidex } from '@/services/dates';
import { accumulateValues, toFlatPropertyMap } from '@/services/questions';
import { getInlandMarineChanges } from '@/validation/formChanges/inlandMarine';
import { getCommercialCrimeChanges } from '@/validation/formChanges/commercialCrime';
import { getCommercialPropertyChanges } from '@/validation/formChanges/commercialProperty';
// import { getGeneralLiabilityChanges } from '@/validation/formChanges/generalLiability';
import { getForm } from '@/services/webservices/forms';
import { generateZodSchema } from '@/services/validators';

export type EffectiveDateChanges = {
	formQuestions: any;
	changedForms: any;
	changedItems: any;
	originalEffectiveDate: Date | undefined;
	newEffectiveDate: Date | undefined;
};

export function isInEffectiveDate( application: any, effectiveDate: any ) {
	if ( !application.validators ) {
		return true;
	}

	const dateValidators = application.validators.filter( ( validator: any ) => validator.fieldType === 'EffectiveDateComparison' );

	if ( dateValidators.length === 0 ) {
		return true;
	}

	return dateValidators.every( ( validator: any ) => {
		return compareDates( getDateFromSquidex( validator.date ), effectiveDate, validator.timePeriod );
	});
}

export async function getDeductibleValueChanges( cppData: any, newEffectiveDate?: Date ): Promise<any> {
	if ( !compareDates( getDateFromSquidex( '06/01/2025 00:00:00' ), newEffectiveDate, 'after' ) ) {
		return [];
	}
	
	let results: any[] = [];
	let mergedResults: any = {};

	for ( const form of [ ...cppData.applications, ...cppData.questionnaires ] ) {
		if ( form.applicationTypeGuid === formIds.INLAND_MARINE ) {
			results = [ ...results, ...getInlandMarineChanges( form.applicationTypeGuid, form.formData ) ];
		}

		if ( form.applicationTypeGuid === formIds.COMMERCIAL_PROPERTY ) {
			results = [ ...results, ...getCommercialPropertyChanges( form.applicationTypeGuid, form.formData ) ];
		}

		if ( form.applicationTypeGuid === formIds.COMMERCIAL_CRIME ) {
			results = [ ...results, ...getCommercialCrimeChanges( form.applicationTypeGuid, form.formData ) ];
		}
	}

	// merge results by parent form
	for ( const result of results ) {
		if ( mergedResults[ result.formGuid ] ) {
			mergedResults[ result.formGuid ].questions.push( result );
		} else {
			mergedResults[ result.formGuid ] = {
				formName: result.parentForm,
				subformPath: result.subformPath,
				formGuid: result.formGuid,
				questions: [ result ]
			};
		}
	}

	return Object.entries( mergedResults ).map( ( [ , formVal ] ) => {
		return formVal;
	});
}

// [NOTE]: This code should not be required, but I'm leaving it here in case we need it later.
// export async function getCyberCoverageChanges( cppData: any, newEffectiveDate?: Date ): Promise<any> {
// 	let results: any[] = [];
// 	let mergedResults: any = {};

// 	for ( const form of [ ...cppData.applications, ...cppData.questionnaires ] ) {
// 		if ( form.applicationTypeGuid === formIds.GENERAL_LIABILITY ) {
// 			results = [ ...results, ...getGeneralLiabilityChanges( form.applicationTypeGuid, form.formData, [ ...cppData.applications, ...cppData.questionnaires ] ) ];
// 		}
// 	}

// 	for ( const result of results ) {
// 		if ( mergedResults[ result.formGuid ] ) {
// 			mergedResults[ result.formGuid ].question.push( result );
// 		} else {
// 			mergedResults[ result.formGuid ] = {
// 				formName: result.parentForm,
// 				subformPath: result.subformPath,
// 				formGuid: result.formGuid,
// 				questions: [ result ]
// 			};
// 		}
// 	}

// 	return Object.entries( mergedResults ).map( ( [ , formVal ] ) => {
// 		return formVal;
// 	});
// }

export async function getEffectiveDateChanges( applications: any[], questionnaires: any[], originalEffectiveDate?: Date, newEffectiveDate?: Date ): Promise<EffectiveDateChanges> {
	let formQuestions: any = {};

	const changedItems: any = [];
	const changedForms: any = [];

	for ( const application of [ ...applications, ...questionnaires ] ) {
		const applicationDetails = await getForm( application.applicationTypeGuid );

		formQuestions[ applicationDetails.id ] = {
			title: applicationDetails.title,
			questions: []
		};

		const originalFormVisibility = applicationDetails.validation.filter( ( validator: any ) => validator.fieldType === 'EffectiveDateComparison' ).every( ( validator: any ) => compareDates( getDateFromSquidex( validator.date ), originalEffectiveDate, validator.timePeriod ) );
		const newFormVisibility = applicationDetails.validation.filter( ( validator: any ) => validator.fieldType === 'EffectiveDateComparison' ).every( ( validator: any ) => compareDates( getDateFromSquidex( validator.date ), newEffectiveDate, validator.timePeriod ) );

		if ( originalFormVisibility !== newFormVisibility ) {
			changedForms.push({
				formId: applicationDetails.id,
				formTitle: applicationDetails.title,
				originalValue: originalFormVisibility,
				newValue: newFormVisibility,
				questionTitle: ''
			});
		}

		accumulateValues( applicationDetails.questions, ( question: any ) => {
			if ( question.validation && question.validation.length > 0 ) {
				const hasDateComparison = question.validation.some( ( validator: any ) => validator.fieldType === 'EffectiveDateComparison' );
				
				if ( hasDateComparison ) {
					formQuestions[ applicationDetails.id ].questions.push( question );
				}
			}
		});
	}

	Object.entries( formQuestions ).forEach( ( [ formKey, formValues ]: [ string, any ] ) => {
		formValues.questions.forEach( ( question: any ) => {
			const dateValidators = question.validation.filter( ( validator: any ) => validator.fieldType === 'EffectiveDateComparison' );

			const originalShow = dateValidators.every( ( validator: any ) => {
				return compareDates( getDateFromSquidex( validator.date ), originalEffectiveDate, validator.timePeriod );
			});

			const newShow = dateValidators.every( ( validator: any ) => {
				return compareDates( getDateFromSquidex( validator.date ), newEffectiveDate, validator.timePeriod );
			});

			if ( originalShow !== newShow ) {
				let questionTitle = question.label || question.title || question.description;

				if ( question.parentLabel ) {
					questionTitle = `${ question.parentLabel } | ${ question.label }`;
				}

				changedItems.push({
					formId: formKey,
					formTitle: formValues.title,
					originalValue: originalShow,
					newValue: newShow,
					questionTitle
				});
			}
		});
	});

	return {
		formQuestions,
		changedForms,
		changedItems,
		originalEffectiveDate,
		newEffectiveDate
	};
}

// get value regardless of value type (object/array/string)
export function getFieldValue( value: any ) {
	if ( !value ) {
		return '';
	}

	if ( typeof value === 'string' || Array.isArray( value ) ) {
		return value;
	}

	// otherwise, it's an object from a select { label: '', value: '' }
	if ( typeof value === 'object' ) {
		return value.value;
	}

	return '';
}

export function isMatchedValue( matchedValue: any, comparisonValue: any ) {
	if ( Array.isArray( matchedValue ) ) {
		return matchedValue.includes( comparisonValue );
	}

	return isEqual( matchedValue, comparisonValue )
}

// because some of the currency values have commas and some don't
export function filterCurrencyString( val: string ) {
	return val.replaceAll( ',', '' );
}

export function formHasDraftStatus( formData: any, formValues: any ) {
	// check if the form itself is in draft status
	if ( formData.isDraft ) {
		return true;
	}

	// also check if there are any subforms in draft status
	// location form:
	const validLocationFormIds = [ formIds.COMMERCIAL_PROPERTY ];
	const validSubcoverageFormIds = [ formIds.INLAND_MARINE, formIds.COMMERCIAL_CRIME ];

	// form ID is in formData.id or formData.applicationTypeGuid depending on the use case
	if ( ( formValues.embeddedFormId && formValues.embeddedFormId.length > 0 ) && ( validLocationFormIds.includes( formData.id ) || validLocationFormIds.includes( formData.applicationTypeGuid ) ) ) {
		// check for both the location forms and the building forms
		let returnValue = false;

		for ( const location of formValues.embeddedFormId ) {
			if ( location.isDraft ) {
				returnValue = true;

				break;
			}

			for ( const building of location?.newLocation?.tableEmbeddedFormBuildings || [] ) {
				if ( building.isDraft ) {
					returnValue = true;

					break;
				}
			}
		}

		return returnValue;
	}

	// commercial crime/commercial inland marine
	if ( formValues.subcoverages && ( validSubcoverageFormIds.includes( formData.id ) || validSubcoverageFormIds.includes( formData.applicationTypeGuid ) ) ) {
		let returnValue = false;
		
		for ( const subForm of formValues.subcoverages ) {
			if ( subForm.isDraft ) {
				returnValue = true;

				break;
			}
		}

		return returnValue;
	}

	return false;
}

function getReplacementGetValues( flattenedValues: any ) {
	return ( path: string | string[] ) => {
		if ( Array.isArray( path ) ) {
			return path.map( pathString => {
				return get( flattenedValues, pathString );
			});
		}

		return get( flattenedValues, path )
	};
}

export async function fullyValidateForms( cppData: any ) {
	let fullValidation: any[] = [];

	const forms: any[] = [ ...cppData.applications, ...cppData.questionnaires ];

	for ( const form of forms ) {
		const formIssues: any[] = [];
		let hasFormsInDraftStatus = false;
		let formIsEmpty = false;

		const formDetails = await getForm( form.applicationTypeGuid );
		const subforms = form?.formData?.subcoverages || form?.formData?.embeddedFormId || [];

		if ( formHasDraftStatus( form, form.formData ) ) {
			hasFormsInDraftStatus = true;
		}

		subforms.forEach( ( subform: any ) => {
			// delete subform specific metadata
			const duplicatedSubform = cloneDeep( subform );

			delete duplicatedSubform.formGuid;
			delete duplicatedSubform.formName;
			delete duplicatedSubform.isDraft;
			delete duplicatedSubform.lastModBy;
			delete duplicatedSubform.lastModDt;

			// if there is no other data besides metadata, the record was
			// added but never saved, so it skipped validation.
			if ( Object.keys( duplicatedSubform ).length === 0 ) {
				formIssues.push({
					formTitle: subform.formName,
					questionId: '',
					message: 'This form has no data. Please review.'
				});
			}
		});

		const flattenedFormValues = toFlatPropertyMap( form.formData );
		const getValues = getReplacementGetValues( flattenedFormValues );

		if ( Object.keys( flattenedFormValues ).length === 0 ) {
			formIsEmpty = true;
		}

		// if the form is empty or has draft statues, we shouldn't validate the rest of it
		// since we know don't have all values and building the schema would be extraneous
		if ( !formIsEmpty && !hasFormsInDraftStatus ) {
			const { zodSchema, questionMap } = generateZodSchema( formDetails, cppData, getValues, () => {} );
	
			const results = zodSchema.safeParse( flattenedFormValues );
	
			if ( !results.success ) {
				const formattedErrors = z.treeifyError( results.error );
				
				Object.entries( formattedErrors.properties as any ).forEach( ( [ key, value ] ) => {
					const matchedQuestion = questionMap.find( question => question.systemId === key.replace( '.value', '' ) );
	
					formIssues.push({
						formTitle: matchedQuestion?.parentForm || '',
						questionId: matchedQuestion?.label || key,
						message: ( value as any ).errors.join( ' || ' )
					});
				});
			}
		}

		// now we need to check if the cyber questionnaire is required
		if ( formDetails.id === formIds.GENERAL_LIABILITY ) {
			const sharedCoverageValue = form.formData?.c2445?.sharedCoverage?.value || form.formData?.cg2601?.sharedCoverage?.value;
			const shouldHaveCyberQuestionnaire = sharedCoverageValue ? Number( filterCurrencyString( sharedCoverageValue ) ) > 10000 : false;
			const hasCyberQuestionnaire = forms.some( a => a.applicationTypeGuid === formIds.CYBER_COVERAGE_QUESTIONNAIRE );
			const cyberLiabilitySelected = form.formData?.optionalCoverageC2445?.yesNo || form.formData?.optionalCoverageC2601?.yesNo;

			if ( cyberLiabilitySelected === 'yes' && !hasCyberQuestionnaire && shouldHaveCyberQuestionnaire ) {
				formIssues.push({
					formTitle: formDetails.formTitle,
					questionId: '',
					message: 'This application requires the Cyber Coverage Questionnaire (C-2271)'
				});
			}
		}

		// if there are any issues, update the applications to set them to draft status
		// this is being removed temporarily because it doesn't seem necessary anymore
		// if ( formIssues.length > 0 || formIsEmpty || hasFormsInDraftStatus ) {
		// 	await updateCppForm( form.cppApplicationId, form.id, form.formData, true );
		// }

		fullValidation.push({
			cppId: form.cppApplicationId,
			formTitle: formDetails.title,
			formId: form.id,
			formIssues,
			formIsEmpty,
			hasFormsInDraftStatus
		});
	}

	return fullValidation;
}
